import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useConfirm } from "../contexts/ConfirmContext";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

const Internships = () => {
  const { confirm } = useConfirm();
  const [internships, setInternships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedInternship, setSelectedInternship] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    company: "",
    companyLogoUrl: "",
    title: "",
    type: "Remote",
    duration: "",
    stipend: "",
    location: "",
    deadline: "",
    certificate: false,
    reference: false,
    applicationLink: "",
  });

  useEffect(() => {
    fetchInternships();
  }, []);

  const fetchInternships = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/internships`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInternships(response.data);
    } catch (error) {
      toast.error("Failed to fetch internships");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const internshipData = {
        ...formData,
        deadline: new Date(formData.deadline).toISOString(),
      };

      if (selectedInternship) {
        await axios.put(
          `${API_URL}/internships/${selectedInternship.id}`,
          internshipData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success("Internship updated successfully");
      } else {
        await axios.post(`${API_URL}/internships`, internshipData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Internship created successfully");
      }
      setShowForm(false);
      setSelectedInternship(null);
      setFormData({
        company: "",
        companyLogoUrl: "",
        title: "",
        type: "Remote",
        duration: "",
        stipend: "",
        location: "",
        deadline: "",
        certificate: false,
        reference: false,
        applicationLink: "",
      });
      fetchInternships();
    } catch (error) {
      toast.error("Failed to save internship");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Delete internship",
      message: "Are you sure you want to delete this internship?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/internships/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Internship deleted successfully");
      if (selectedInternship && selectedInternship.id === id)
        setSelectedInternship(null);
      fetchInternships();
    } catch (error) {
      toast.error("Failed to delete internship");
    }
  };

  const handleEdit = (internship) => {
    setSelectedInternship(internship);
    setFormData({
      company: internship.company,
      companyLogoUrl: internship.companyLogoUrl || "",
      title: internship.title,
      type: internship.type,
      duration: internship.duration,
      stipend: internship.stipend || "",
      location: internship.location,
      deadline: internship.deadline
        ? new Date(internship.deadline).toISOString().slice(0, 16)
        : "",
      certificate: internship.certificate,
      reference: internship.reference,
      applicationLink: internship.applicationLink || "",
    });
    setShowForm(true);
  };

  const filteredInternships = internships.filter(
    (internship) =>
      internship.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      internship.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
      internship.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-64px)] bg-gray-50 rounded-lg shadow overflow-hidden">
      {/* Left Column: Internship List */}
      <div className="w-1/4 min-w-[260px] bg-white border-r flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <span className="font-semibold text-lg">Internships</span>
          <button
            className="flex items-center gap-1 px-2 py-1 text-sm bg-teal-600 text-white rounded hover:bg-teal-700"
            onClick={() => {
              setShowForm(true);
              setSelectedInternship(null);
              setFormData({
                company: "",
                companyLogoUrl: "",
                title: "",
                type: "Remote",
                duration: "",
                stipend: "",
                location: "",
                deadline: "",
                certificate: false,
                reference: false,
                applicationLink: "",
              });
            }}
          >
            <PlusIcon className="h-4 w-4" /> Add
          </button>
        </div>
        <div className="p-3">
          <input
            type="text"
            placeholder="Search internships"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {filteredInternships.length === 0 && (
            <div className="text-gray-400 text-center mt-8">
              No internships found
            </div>
          )}
          {filteredInternships.map((internship) => (
            <div
              key={internship.id}
              className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b hover:bg-teal-50 ${
                selectedInternship && selectedInternship.id === internship.id
                  ? "bg-teal-100"
                  : ""
              }`}
              onClick={() => {
                setSelectedInternship(internship);
                setShowForm(false);
              }}
            >
              <div className="flex-shrink-0 w-10 h-10 rounded flex items-center justify-center text-sm font-bold text-teal-700 overflow-hidden border">
                {internship.companyLogoUrl ? (
                  <img
                    src={internship.companyLogoUrl}
                    alt={internship.company}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <span className="text-xs">{internship.company[0]}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{internship.title}</div>
                <div className="text-xs text-gray-500 truncate">
                  {internship.company} - {internship.location}
                </div>
              </div>
              <button
                className="p-1 text-gray-400 hover:text-teal-600"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(internship);
                }}
                title="Edit"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              <button
                className="p-1 text-gray-400 hover:text-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(internship.id);
                }}
                title="Delete"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Middle Column: Internship Details or Form */}
      <div className="w-2/4 min-w-[340px] bg-gray-50 flex flex-col">
        <div className="flex-1 overflow-y-auto p-8">
          {showForm ? (
            <form
              onSubmit={handleSubmit}
              className="bg-white shadow rounded-lg p-6 max-w-xl mx-auto"
            >
              <h2 className="text-xl font-semibold mb-4">
                {selectedInternship
                  ? "Edit Internship"
                  : "Create New Internship"}
              </h2>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Company
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Company Logo URL
                  </label>
                  <input
                    type="text"
                    name="companyLogoUrl"
                    value={formData.companyLogoUrl}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Type
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    required
                  >
                    <option value="Remote">Remote</option>
                    <option value="On-site">On-site</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Duration
                  </label>
                  <input
                    type="text"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Stipend
                  </label>
                  <input
                    type="text"
                    name="stipend"
                    value={formData.stipend}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Deadline
                  </label>
                  <input
                    type="datetime-local"
                    name="deadline"
                    value={formData.deadline}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    required
                  />
                </div>
                <div>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      name="certificate"
                      checked={formData.certificate}
                      onChange={handleInputChange}
                      className="rounded border-gray-300 text-teal-600 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Certificate Provided
                    </span>
                  </label>
                </div>
                <div>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      name="reference"
                      checked={formData.reference}
                      onChange={handleInputChange}
                      className="rounded border-gray-300 text-teal-600 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Reference Provided
                    </span>
                  </label>
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Application Link
                  </label>
                  <input
                    type="text"
                    name="applicationLink"
                    value={formData.applicationLink}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setSelectedInternship(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {selectedInternship
                    ? "Update Internship"
                    : "Create Internship"}
                </button>
              </div>
            </form>
          ) : selectedInternship ? (
            <div className="bg-white shadow rounded-lg p-8 max-w-xl mx-auto">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded flex items-center justify-center text-xl font-bold text-teal-700 overflow-hidden border">
                  {selectedInternship.companyLogoUrl ? (
                    <img
                      src={selectedInternship.companyLogoUrl}
                      alt={selectedInternship.company}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <span className="text-sm">
                      {selectedInternship.company[0]}
                    </span>
                  )}
                </div>
                <div>
                  <h2 className="text-2xl font-bold">
                    {selectedInternship.title}
                  </h2>
                  <div className="text-gray-500">
                    {selectedInternship.company}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div>
                  <div className="text-xs text-gray-400">Type</div>
                  <div>{selectedInternship.type}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Duration</div>
                  <div>{selectedInternship.duration}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Location</div>
                  <div>{selectedInternship.location}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Stipend</div>
                  <div>{selectedInternship.stipend || "N/A"}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Deadline</div>
                  <div>
                    {selectedInternship.deadline
                      ? new Date(
                          selectedInternship.deadline
                        ).toLocaleDateString()
                      : "N/A"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Certificate</div>
                  <div>{selectedInternship.certificate ? "Yes" : "No"}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Reference</div>
                  <div>{selectedInternship.reference ? "Yes" : "No"}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs text-gray-400">Application Link</div>
                  <div>
                    <a
                      href={selectedInternship.applicationLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-600 hover:underline break-all"
                    >
                      {selectedInternship.applicationLink || "N/A"}
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => handleEdit(selectedInternship)}
                  className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(selectedInternship.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          ) : // Initial state or after deleting a selected internship
          loading ? null : (
            <div className="flex items-center justify-center h-full text-gray-400">
              Select an internship to view details or click Add to create a new
              one.
            </div>
          )}
        </div>
      </div>

      {/* Right Column: Internship Options (future features) */}
      <div className="w-1/4 min-w-[260px] bg-white border-l flex flex-col">
        <div className="p-4 border-b font-semibold text-lg">
          Internship Options
        </div>
        <div className="p-4">
          {selectedInternship ? (
            <ul className="space-y-3 text-gray-600">
              <li>View Applicants (Coming Soon)</li>
              {/* Add more options as needed */}
            </ul>
          ) : (
            <div className="text-gray-400">
              Select an internship to see options
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Internships;
